import { createListenerMiddleware } from '@reduxjs/toolkit';
import { addCommentItemID, deleteCommentItemID, resetUIState } from '../stores/UISlice.js';
import { syncCommentsForItem } from './CommentSync.js';

export const COMMENT_SYNC_POLL_INTERVAL_MS = 20_000;

export const commentSyncListenerMiddleware = createListenerMiddleware();

const activeItemIds = new Set<string>();
const pollHandles = new Map<string, ReturnType<typeof setInterval>>();

const stopSyncing = (itemId: string) => {
  activeItemIds.delete(itemId);

  const handle = pollHandles.get(itemId);
  if (handle) {
    clearInterval(handle);
    pollHandles.delete(itemId);
  }
};

const logSyncError = (itemId: string, error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`comment sync: sync cycle failed for item ${itemId}`, error);
};

commentSyncListenerMiddleware.startListening({
  actionCreator: addCommentItemID,
  effect: async (action) => {
    const itemId = action.payload;

    // set the guard synchronously, before any `await`, so two rapid
    // addCommentItemID dispatches for the same id can't both pass it
    if (activeItemIds.has(itemId)) return;
    activeItemIds.add(itemId);

    await syncCommentsForItem(itemId).catch((error) => logSyncError(itemId, error));

    // a deleteCommentItemID for this id may have arrived while the sync
    // above was in flight - don't start an orphaned poller for it
    if (!activeItemIds.has(itemId)) return;

    const handle = setInterval(() => {
      void syncCommentsForItem(itemId).catch((error) => logSyncError(itemId, error));
    }, COMMENT_SYNC_POLL_INTERVAL_MS);
    pollHandles.set(itemId, handle);
  },
});

commentSyncListenerMiddleware.startListening({
  actionCreator: deleteCommentItemID,
  effect: (action) => {
    stopSyncing(action.payload);
  },
});

commentSyncListenerMiddleware.startListening({
  actionCreator: resetUIState,
  effect: () => {
    for (const itemId of Array.from(activeItemIds)) {
      stopSyncing(itemId);
    }
  },
});
